import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Trash2, Upload } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, cn, toast } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { notifyError } from "./Upgrade.tsx";

export type MediaAsset = { id: string; url: string; thumbUrl: string; width: number | null; height: number | null; purpose: string; originalName: string | null };

export async function uploadImage(file: File, purpose: "image" | "logo" = "image") {
  const form = new FormData();
  form.append("purpose", purpose);
  form.append("file", file);
  const res = await api.post<{ asset: { id: string; url: string; thumbUrl: string | null } }>("/media", form);
  return res.asset;
}

/** Imperative picker used by the editor and forms. */
export function useMediaPicker() {
  const [state, setState] = useState<{ open: boolean; multiple: boolean; resolve?: (v: MediaAsset[] | null) => void }>({ open: false, multiple: false });
  const pick = (opts: { multiple: boolean }) => new Promise<MediaAsset[] | null>((resolve) => setState({ open: true, multiple: opts.multiple, resolve }));
  const element = (
    <MediaPickerDialog
      open={state.open}
      multiple={state.multiple}
      onClose={(v) => {
        state.resolve?.(v);
        setState({ open: false, multiple: false });
      }}
    />
  );
  return { pick, element };
}

function MediaPickerDialog({ open, multiple, onClose }: { open: boolean; multiple: boolean; onClose: (v: MediaAsset[] | null) => void }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const assets = useQuery({ queryKey: ["media"], queryFn: () => api.get<{ assets: MediaAsset[] }>("/media").then((r) => r.assets), enabled: open });
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const f of Array.from(files).slice(0, 12)) {
        const a = await uploadImage(f);
        uploaded.push(a.id);
      }
      await qc.invalidateQueries({ queryKey: ["media"] });
      setSelected((s) => (multiple ? [...s, ...uploaded] : uploaded.slice(-1)));
      toast.success(`Uploaded ${uploaded.length} image${uploaded.length === 1 ? "" : "s"}`);
    } catch (err) {
      notifyError(err);
    } finally {
      setUploading(false);
    }
  };
  const remove = async (id: string) => {
    await api.del(`/media/${id}`);
    await qc.invalidateQueries({ queryKey: ["media"] });
    setSelected((s) => s.filter((x) => x !== id));
  };
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : multiple ? [...s, id] : [id]));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(null)}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>Media library</DialogTitle>
          <DialogDescription>Upload JPEG, PNG, WebP or AVIF images up to 10 MB. Photos are resized and stripped of location data.</DialogDescription>
        </DialogHeader>
        <div
          className="max-h-[55vh] overflow-y-auto"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void onFiles(e.dataTransfer.files);
          }}
        >
          {assets.data?.length === 0 ? (
            <EmptyState icon={<ImagePlus />} title="No images yet" description="Drop images here or upload from your device." />
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
              {assets.data?.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => toggle(a.id)}
                  onDoubleClick={() => onClose([a])}
                  className={cn("group relative aspect-square overflow-hidden rounded-lg border bg-muted ring-offset-2 transition", selected.includes(a.id) && "ring-2 ring-primary")}
                >
                  <img src={a.thumbUrl} alt={a.originalName ?? ""} className="size-full object-cover" loading="lazy" />
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      void remove(a.id);
                    }}
                    className="absolute top-1.5 right-1.5 rounded-md bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100"
                    aria-label="Delete image"
                  >
                    <Trash2 className="size-3.5" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="sm:justify-between">
          <input ref={input} type="file" accept="image/*" multiple className="hidden" onChange={(e) => void onFiles(e.target.files)} />
          <Button variant="outline" onClick={() => input.current?.click()} loading={uploading}>
            <Upload /> Upload
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onClose(null)}>
              Cancel
            </Button>
            <Button disabled={!selected.length} onClick={() => onClose((assets.data ?? []).filter((a) => selected.includes(a.id)))}>
              Use {selected.length > 1 ? `${selected.length} images` : "image"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
